import { Base } from "ui/Scripts/source/framework/base";
import { EditItem, IEditItem } from "ui/Scripts/source/models/common/editItem";
import { EnumHelper } from "ui/Scripts/source/models/enums";
import { IUnitModuleNodeUsage, UnitModuleNodeUsage } from "./unitModuleNodeUsage";

export interface IUnitModuleNode extends IEditItem {
    name: string;
    comment: string;
    children: IUnitModuleNode[];
    usages: IUnitModuleNodeUsage[];
    parent: IUnitModuleNode;
    level: number;
    hidden: boolean;
    collapsed: boolean;

    getIsRoot(): boolean;
    getHasChildren(): boolean;
    getParentIsRoot(): boolean;
    getIsHidden(): boolean;
    getAncesterIsCollapsed(): boolean;
    getIsDisabled(): boolean;
    getSiblings(): IUnitModuleNode[];
    getSiblingNames(lowerCase: boolean): string[];
    getUnitModuleNodeById(id: string): IUnitModuleNode;
    getAsList(list: IUnitModuleNode[], level: number): IUnitModuleNode[];
    getDescendantIdsForRemoveSub(ids: string[]);
    getDescendantIdsForRemove(): string[];
    setUsagesSub(ids: string[], usages: IUnitModuleNodeUsage[]): string[];
    setUsages(usages: IUnitModuleNodeUsage[]): string[];
    setCollapsed(value: boolean);
    addUnitModuleNode(node: IUnitModuleNode);
    removeUnitModuleNodeById(id: string): string[];
    setApplicationPermissionTypeVisible(applicationPermissionType: number, value: boolean);
    setNotInUseNodesHidden(applicationPermissionType: number, value: boolean);
}

export class UnitModuleNode extends EditItem implements IUnitModuleNode {
    name: string;
    comment: string;
    children: UnitModuleNode[];
    usages: UnitModuleNodeUsage[];
    parent: UnitModuleNode;
    level: number;
    hidden: boolean;
    collapsed: boolean;

    constructor();
    constructor(obj: IUnitModuleNode);
    constructor(obj?: any) {
        super(obj);
        this.name = obj && obj.name || "";
        this.comment = obj && obj.comment || "";
        this.level = obj && obj.level || 0;
        this.hidden = obj && obj.hidden || false;
        this.collapsed = obj && obj.collapsed || false;
        this.children = [];
        this.parent = null;
        if (obj && obj.children) {
            this.children = Base.getTypedArray(obj.children, UnitModuleNode);
            for (const child of this.children) {
                child.parent = this;
            }
        }
        this.usages = [];
        if (obj && obj.usages) {
            this.usages = Base.getTypedArray(obj ? obj.usages : null, UnitModuleNodeUsage);
        }
    }

    getIsRoot(): boolean {
        return !this.parent;
    }

    getHasChildren(): boolean {
        return this.children.length > 0;
    }

    getParentIsRoot(): boolean {
        if (!this.parent) return false;
        return this.parent.getIsRoot();
    }

    getAncesterIsCollapsed(): boolean {
        if (!this.parent) return false;
        if (this.parent.collapsed) return true;
        return this.parent.getAncesterIsCollapsed();
    }

    getIsHidden(): boolean {
        if (this.hidden) return true;
        if (!this.parent) return false;
        return this.parent.getIsHidden();
    }

    getSiblings(): IUnitModuleNode[] {
        if (this.getIsRoot()) return [];
        return this.parent.children.filter(i => i.id !== this.id);
    }

    getUnitModuleNodeById(id: string): IUnitModuleNode {
        if (!id) return null;
        if (this.id.equalIgnoreCase(id)) return this;
        for (const child of this.children) {
            const childResult = child.getUnitModuleNodeById(id);
            if (childResult) return childResult;
        }
        return null;
    }

    getIsDisabled(): boolean {
        return this.usages.filter(i => i.visible && i.inUse).length < 1;
    }

    getSiblingNames(lowerCase: boolean): string[] {
        if (!this.parent) return [];
        return this.parent.children.filter(i => i.id !== this.id).map(i => lowerCase ? i.name.toLowerCase() : i.name);
    }

    getDescendantIdsForRemoveSub(ids: string[]): string[] {
        if (this.children.length < 1) return ids;
        for (const child of this.children) {
            child.getDescendantIdsForRemoveSub(ids);
        }
        for (const child of this.children) {
            ids.push(child.id);
        }
        return ids;
    }

    getDescendantIdsForRemove(): string[] {
        return this.getDescendantIdsForRemoveSub([]);
    }

    getAsList(list: IUnitModuleNode[], level: number = 0): IUnitModuleNode[] {
        list.push(this);
        this.level = level;
        if (this.children.length < 1) return list;
        for (const child of this.children) {
            child.getAsList(list, level + 1);
        }
        return list;
    }

    setCollapsed(value: boolean) {
        this.collapsed = value;
    }

    setUsagesSub(ids: string[], usages: IUnitModuleNodeUsage[]): string[] {
        let usageSet = false;
        for (const newUsage of usages) {
            const usage = this.usages.find(i => EnumHelper.isEqual(i.applicationPermissionType, newUsage.applicationPermissionType));
            if (!usage) continue;
            usage.inUse = newUsage.inUse;
            usageSet = true;
        }
        if (usageSet) {
            ids.push(this.id);
        }
        for (const child of this.children) {
            child.setUsagesSub(ids, usages);
        }
        return ids;
    }

    setUsages(usages: IUnitModuleNodeUsage[]): string[] {
        return this.setUsagesSub([], usages);
    }

    addUnitModuleNode(node: IUnitModuleNode) {
        if (!node) return;
        node.parent = this;
        const index = this.children.findIndex(i => i.name && i.name.localeCompare(node.name, appConfig.culture) > 0);
        if (index < 0) {
            this.children.push(node);
        } else {
            this.children.splice(index, 0, node);
        }
    }

    removeUnitModuleNodeById(id: string): string[] {
        const unitModuleNode = this.getUnitModuleNodeById(id);
        if (!unitModuleNode || !unitModuleNode.parent) return [];
        const result = unitModuleNode.getDescendantIdsForRemove().concat([unitModuleNode.id]);
        unitModuleNode.parent.children = unitModuleNode.parent.children.filter(i => i.id !== unitModuleNode.id);
        return result;
    }
    
    setApplicationPermissionTypeVisible(applicationPermissionType: number, value: boolean) {
        for (const usage of this.usages) {
            if (!EnumHelper.isEqual(applicationPermissionType, usage.applicationPermissionType)) continue;
            usage.visible = value;
        }
        for (const child of this.children) {
            child.setApplicationPermissionTypeVisible(applicationPermissionType, value);
        }
    }
        
    setNotInUseNodesHidden(applicationPermissionType: number, value: boolean) {
        for (const usage of this.usages) {
            if (!EnumHelper.isEqual(applicationPermissionType, usage.applicationPermissionType)) continue;
            this.hidden = !usage.inUse && value;
        }
        for (const child of this.children) {
            child.setNotInUseNodesHidden(applicationPermissionType, value);
        }
    }
}